import React, { useState } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";
import Window, { Footer } from "../../components/Window";
import Icon from "../../components/Icon";

// Emails a password reset link to the address the account signed up with.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"form" | "sending" | "sent">("form");
  const [error, setError] = useState("");

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setState("sending");
    const address = email.trim();
    try {
      // After resetting, the link brings people back to sign in here.
      await sendPasswordResetEmail(auth, address, {
        url: `${window.location.origin}/signin`,
      }).catch(async (err) => {
        // If this site's address isn't on Firebase's allowed list yet,
        // send the plain email instead of failing.
        if (err?.code === "auth/unauthorized-continue-uri") {
          await sendPasswordResetEmail(auth, address);
        } else {
          throw err;
        }
      });
      setState("sent");
    } catch (err: any) {
      if (err?.code === "auth/user-not-found") {
        // Don't reveal whether an email has an account.
        setState("sent");
        return;
      }
      setError(
        err?.code === "auth/invalid-email"
          ? "That doesn't look like an email address."
          : err?.code === "auth/too-many-requests"
          ? "Too many tries. Please wait a little and try again."
          : "Sorry, that didn't work. Please try again."
      );
      setState("form");
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="mb-5 text-center">
          <span className="logo">
            <Icon name="mail" size={37} tone="pink" pop className="mr-1" />
            PenPal Board
            <span className="logo-sub">the 2SLGBTQIA+ pen pal network</span>
          </span>
        </div>

        <Window title="forgot_password.exe" tone="blue" minimizable={false}>
          {state === "sent" ? (
            <div className="flex flex-col gap-3">
              <h1 className="window-heading">Check your mail ✉</h1>
              <p>
                If there's an account for <strong>{email.trim()}</strong>, we've
                sent it a link to choose a new password.
              </p>
              <p className="text-sm text-ink-soft">
                It can take a few minutes. If you don't see it, check your spam
                or junk folder.
              </p>
              <Link to="/signin" className="btn">
                <Icon name="back" size={16} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={send} className="flex flex-col gap-4">
              <h1 className="window-heading">Forgot your password?</h1>
              <p className="window-sub !mb-0">
                It happens to all of us. Enter the email you signed up with and
                we'll send you a link to reset it.
              </p>
              <label className="field">
                <span className="field-label">Email</span>
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>
              {error && <p className="error-text">{error}</p>}
              <button type="submit" className="btn w-full" disabled={state === "sending"}>
                {state === "sending" ? "Sending..." : "Send reset link ✿"}
              </button>
              <p className="text-center text-sm">
                Remembered it? <Link to="/signin">Sign in</Link>
              </p>
            </form>
          )}
        </Window>

        <Footer />
      </div>
    </div>
  );
}
